import React, { useEffect, useRef, useState } from 'react';
import { useAppContext, useClock } from '../context/AppContext';
import { classNames, formatCedi, relativeTimeFrom, sanitizeNumeric, sanitizeText } from '../utils/formatters';
import { methodLabels, ORDER_SOURCES, ONLINE_SOURCE_CHANNELS, DISCOUNT_REASONS } from '../utils/constants';
import OrderFAB from '../components/OrderFAB';
import OrderDrawer from '../components/OrderDrawer';

const isDeliveryOrder = (order) => order?.orderType !== 'in-person';
const getReadyOrderCompletionStatus = (order) => (isDeliveryOrder(order) ? 'delivered' : 'picked_up');
const getReadyOrderCompletionLabel = (order) => (isDeliveryOrder(order) ? 'Delivered' : 'Picked up');

const PosUiIcon = ({ name, className = 'h-4 w-4' }) => {
  const sharedProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
  };

  switch (name) {
    case 'search':
      return <svg {...sharedProps}><circle cx="11" cy="11" r="6" /><path d="m20 20-3.5-3.5" /></svg>;
    case 'close':
      return <svg {...sharedProps}><path d="m6 6 12 12" /><path d="M18 6 6 18" /></svg>;
    case 'online':
      return <svg {...sharedProps}><circle cx="12" cy="12" r="8" /><path d="M4 12h16" /><path d="M12 4a12 12 0 0 1 0 16" /><path d="M12 4a12 12 0 0 0 0 16" /></svg>;
    case 'store':
      return <svg {...sharedProps}><path d="M4 10.5 5.5 5h13L20 10.5" /><path d="M5 10v8.5a1.5 1.5 0 0 0 1.5 1.5H9v-5h6v5h2.5a1.5 1.5 0 0 0 1.5-1.5V10" /></svg>;
    case 'momo':
      return <svg {...sharedProps}><rect x="7" y="3" width="10" height="18" rx="2" /><path d="M11 18h2" /></svg>;
    case 'cash':
      return <svg {...sharedProps}><rect x="3" y="7" width="18" height="10" rx="2" /><path d="M8 12h8" /><circle cx="12" cy="12" r="1.5" /></svg>;
    case 'card':
      return <svg {...sharedProps}><rect x="3" y="6" width="18" height="12" rx="2" /><path d="M3 10h18" /><path d="M7 14h4" /></svg>;
    case 'split':
      return <svg {...sharedProps}><path d="M8 4 4 8l4 4" /><path d="M4 8h8a4 4 0 0 1 4 4v8" /><path d="m16 20 4-4-4-4" /><path d="M20 16h-8a4 4 0 0 1-4-4V4" /></svg>;
    case 'promotions':
      return <svg {...sharedProps}><path d="M12 3v18" /><path d="M6 7h12" /><path d="M7 12h10" /><path d="M8 17h8" /></svg>;
    case 'mainPackages':
      return <svg {...sharedProps}><path d="M4 7h16" /><path d="M6 7l1 10a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-10" /><path d="M9 11h6" /><path d="M10 15h4" /></svg>;
    case 'extras':
      return <svg {...sharedProps}><path d="M8 5h8" /><path d="M10 5v5a4 4 0 1 0 4 0V5" /><path d="M6 19h12" /></svg>;
    default:
      return <svg {...sharedProps}><path d="M5 8h14" /><path d="M7 8l1 10a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2l1-10" /><path d="M9 12h6" /></svg>;
  }
};

export default function POSTab() {
  const {
    isMobile,
    showMobileCart,
    setShowMobileCart,
    currentBundles,
    activeBundleId,
    setActiveBundleId,
    updateBundleName,
    updateOrderQuantity,
    removeBundle,
    addNewBundle,
    clearCurrentOrder,
    calcBundleTotal,
    updateBundlePackaging,
    packagingCost,
    grandTotal,
    discountPercent,
    setDiscountPercent,
    discountReason,
    setDiscountReason,
    discountReasonDetail,
    setDiscountReasonDetail,
    discountAmount,
    canApplyDiscountDirectly,
    maxDiscountPercent,
    posPaymentMethod,
    setPosPaymentMethod,
    splitPayments,
    setSplitPayments,
    setShowSplitPaymentModal,
    processOrder,
    isProcessing,
    orderType,
    setOrderType,
    orderSource,
    setOrderSource,
    orderSourceDetail,
    setOrderSourceDetail,
    customerName,
    setCustomerName,
    customerPhone,
    setCustomerPhone,
    deliveryAddress,
    setDeliveryAddress,
    deliveryFeeInput,
    setDeliveryFeeInput,
    notes,
    setNotes,
    menuSearch,
    setMenuSearch,
    filteredMain,
    filteredExtras,
    filteredPromotions,
    renderMenuSection,
    getOrdersFor,
    showOrderDetailsModal,
    updateOrderStatus,
    openRiderAssignmentModal,
    getOrderAssignedRider,
    showToast,
  } = useAppContext();
  const { now } = useClock();

  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedReadyOrderIds, setSelectedReadyOrderIds] = useState(new Set());
  // Channel picker anchored under the "Online" source pill. Holds {left, top}
  // viewport coordinates while open (fixed positioning escapes the source
  // row's horizontal-scroll clipping), or null when closed.
  const [channelMenuPos, setChannelMenuPos] = useState(null);
  const onlineSourceBtnRef = useRef(null);
  const readyOrders = getOrdersFor('ready');

  const openChannelMenu = () => {
    const rect = onlineSourceBtnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const menuWidth = 160;
    setChannelMenuPos({
      left: Math.max(8, Math.min(rect.left, window.innerWidth - menuWidth - 8)),
      top: rect.bottom + 4,
    });
  };

  // The menu is fixed-positioned, so any scroll/resize would detach it from
  // its anchor pill — just close it instead.
  useEffect(() => {
    if (!channelMenuPos) return undefined;
    const close = () => setChannelMenuPos(null);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [channelMenuPos]);

  const totalItems = currentBundles.reduce((sum, bundle) => (
    sum + Object.values(bundle.items).reduce((qty, item) => qty + (item.quantity || 0), 0)
  ), 0);
  const activeBundle = currentBundles.find((bundle) => bundle.id === activeBundleId) || currentBundles[0];
  const displayBundles = activeBundle
    ? [activeBundle, ...currentBundles.filter((bundle) => bundle.id !== activeBundle.id)]
    : currentBundles;

  const categories = [
    { id: 'all', label: 'All Items', icon: 'all' },
    { id: 'promotions', label: 'Promos', icon: 'promotions' },
    { id: 'mainPackages', label: 'Main', icon: 'mainPackages' },
    { id: 'extras', label: 'Extras', icon: 'extras' },
  ];

  // Prune selections that are no longer "ready". getOrdersFor returns a fresh
  // array each render, so this effect runs every render — return the SAME set
  // reference when nothing changed so React bails out instead of looping.
  useEffect(() => {
    const readyIds = new Set(readyOrders.map((order) => order.id));
    setSelectedReadyOrderIds((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (readyIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [readyOrders]);

  const areAllReadyOrdersSelected = readyOrders.length > 0 && selectedReadyOrderIds.size === readyOrders.length;

  const toggleReadyOrderSelection = (orderId) => {
    setSelectedReadyOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAllReadyOrders = () => {
    setSelectedReadyOrderIds((prev) => (
      prev.size === readyOrders.length
        ? new Set()
        : new Set(readyOrders.map((order) => order.id))
    ));
  };

  const completeReadyOrders = (orderIds) => {
    const targets = readyOrders.filter((order) => orderIds.includes(order.id));
    if (!targets.length) return;

    targets.forEach((order) => {
      updateOrderStatus(order.id, getReadyOrderCompletionStatus(order));
    });

    setSelectedReadyOrderIds(new Set());

    const deliveredCount = targets.filter((order) => isDeliveryOrder(order)).length;
    const pickedUpCount = targets.length - deliveredCount;
    const completedParts = [];
    if (deliveredCount > 0) completedParts.push(`${deliveredCount} delivered`);
    if (pickedUpCount > 0) completedParts.push(`${pickedUpCount} picked up`);

    showToast(
      completedParts.length > 0
        ? `Ready orders completed: ${completedParts.join(', ')}`
        : 'Ready orders completed',
      'success'
    );
  };

  // A discount is entirely optional. The "Reason *" asterisk is only truthful
  // once a percentage has actually been entered — shown unconditionally it read
  // as "you must pick a reason to place any order", which is backwards.
  const discountEntered = Number(discountPercent) > 0;
  const cartIsEmpty = currentBundles.every(
    (bundle) => Object.values(bundle.items).filter((item) => item.quantity > 0).length === 0,
  );

  const renderCart = () => (
    <>
      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4 md:px-5">
        {displayBundles.map((bundle) => (
          <div
            key={bundle.id}
            className="rounded-xl border border-gray-100"
            style={{ backgroundColor: 'var(--bg-surface-hover)', borderColor: 'var(--border-primary)' }}
          >
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border-primary)' }}>
              <input
                type="text"
                value={bundle.name}
                onChange={(e) => updateBundleName(bundle.id, e.target.value)}
                className="font-semibold text-gray-800 text-sm bg-transparent border-none focus:outline-none focus:ring-0 w-28 min-w-0"
              />
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setActiveBundleId(bundle.id)}
                  className={classNames(
                    'text-xs font-medium px-2 py-1 rounded-md transition-colors',
                    bundle.id === activeBundleId ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:text-gray-700',
                  )}
                >
                  {bundle.id === activeBundleId ? 'Active' : 'Edit'}
                </button>
                {currentBundles.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeBundle(bundle.id)}
                    className="text-xs text-red-400 hover:text-red-600 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>

            <div className="px-4 py-2 space-y-2">
              {Object.keys(bundle.items).filter((key) => bundle.items[key].quantity > 0).map((orderKey) => {
                const item = bundle.items[orderKey];
                return (
                  <div key={orderKey} className="order-item">
                    <div className="order-item-top">
                      <span className="order-item-name">{item.name}</span>
                      <span className="order-item-line-total">
                        {formatCedi(item.quantity * item.price)}
                      </span>
                    </div>
                    <div className="order-item-bottom">
                      <span className="order-item-unit">{formatCedi(item.price)} each</span>
                      <div className="qty-stepper">
                        <button
                          type="button"
                          onClick={() => updateOrderQuantity(bundle.id, orderKey, item.quantity - 1)}
                          aria-label={`Decrease quantity for ${item.name}`}
                        >
                          −
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button
                          type="button"
                          onClick={() => updateOrderQuantity(bundle.id, orderKey, item.quantity + 1)}
                          aria-label={`Increase quantity for ${item.name}`}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {Object.values(bundle.items).filter((item) => item.quantity > 0).length === 0 && (
                <div className="py-6" />
              )}
            </div>

            {Object.values(bundle.items).some((item) => item.quantity > 0) && (
              <>
                <div className="px-4 py-2 border-t" style={{ borderColor: 'var(--border-primary)' }}>
                  <label className="block text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Packaging</label>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[{ key: 'rubber', label: 'Rubber Bag', cost: 0 }, { key: 'paper', label: 'Paper Bag', cost: 5 }].map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => updateBundlePackaging(bundle.id, option.key)}
                        className={classNames(
                          'px-2 py-1.5 rounded-lg text-[11px] font-medium border transition-all',
                          (bundle.packaging || 'rubber') === option.key ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300',
                        )}
                      >
                        {option.label} {option.cost > 0 && `(${formatCedi(option.cost)})`}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="px-4 py-2 border-t text-right" style={{ borderColor: 'var(--border-primary)' }}>
                  <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>Subtotal: </span>
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>
                    {formatCedi(calcBundleTotal(bundle.items, bundle.packaging))}
                  </span>
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      <div className="border-t px-4 py-4 md:px-5 space-y-3" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide" htmlFor="pos-discount-percent">
              {canApplyDiscountDirectly ? 'Discount' : 'Request discount'}
              <span className="ml-1 normal-case font-medium text-gray-400">(optional)</span>
            </label>
            {!canApplyDiscountDirectly && (
              <span className="text-[10px] text-gray-400 font-medium">Max {maxDiscountPercent}% · needs approval</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative w-24 shrink-0">
              <input
                id="pos-discount-percent"
                type="number"
                min="0"
                max={maxDiscountPercent}
                step="1"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(sanitizeNumeric(e.target.value, { min: 0, max: maxDiscountPercent, fallback: '' }))}
                className="w-full pl-3 pr-7 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="0"
                aria-label={canApplyDiscountDirectly ? 'Discount percentage' : `Requested discount percentage, maximum ${maxDiscountPercent}%`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">%</span>
            </div>
            <select
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              disabled={!(Number(discountPercent) > 0)}
              className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-400"
              aria-label={discountEntered ? 'Discount reason (required)' : 'Discount reason (only needed if you enter a discount)'}
            >
              <option value="">{discountEntered ? 'Reason *' : 'Reason'}</option>
              {DISCOUNT_REASONS.map((entry) => (
                <option key={entry.key} value={entry.key}>{entry.label}</option>
              ))}
            </select>
          </div>
          {Number(discountPercent) > 0 && discountReason === 'other' && (
            <input
              type="text"
              value={discountReasonDetail}
              onChange={(e) => setDiscountReasonDetail(sanitizeText(e.target.value, 120))}
              className="mt-2 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="Specify reason *"
              aria-label="Specify discount reason (required)"
              maxLength={120}
            />
          )}
          {Number(discountPercent) > 0 && !canApplyDiscountDirectly && (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
              The customer pays full price until an admin approves this. Payment stays locked in the queue.
            </p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Payment</label>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-2">
            {[{ key: 'momo', label: 'MoMo', icon: 'momo' }, { key: 'cash', label: 'Cash', icon: 'cash' }, { key: 'card', label: 'Card', icon: 'card' }].map((method) => (
              <button
                key={method.key}
                type="button"
                onClick={() => {
                  setPosPaymentMethod(method.key);
                  setSplitPayments([]);
                }}
                className={classNames(
                  'flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium border-2 transition-all',
                  posPaymentMethod === method.key ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
                )}
              >
                <PosUiIcon name={method.icon} className="h-3.5 w-3.5" />
                {method.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setShowSplitPaymentModal(true)}
            className={classNames(
              'flex w-full items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border-2 transition-all',
              posPaymentMethod === 'split' ? 'bg-indigo-50 text-indigo-700 border-indigo-300' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300',
            )}
          >
            <PosUiIcon name="split" className="h-4 w-4" />
            Split Payment
            {posPaymentMethod === 'split' && splitPayments.length > 0 && (
              <span className="ml-1 text-[10px]">({splitPayments.length})</span>
            )}
          </button>
        </div>

        <div className="order-total-section rounded-xl p-4 space-y-1.5" style={{ backgroundColor: 'var(--bg-base)' }}>
        {packagingCost > 0 && (
          <div className="flex justify-between text-xs text-gray-500">
            <span>Packaging</span>
            <span>{formatCedi(packagingCost)}</span>
          </div>
        )}
        {orderType === 'online' && Number(deliveryFeeInput || 0) > 0 && (
          <div className="flex justify-between text-xs text-gray-500">
            <span>Delivery fee</span>
            <span>{formatCedi(deliveryFeeInput)}</span>
          </div>
        )}
        {Number(discountPercent) > 0 && (
          canApplyDiscountDirectly ? (
            <div className="flex justify-between text-xs font-medium text-emerald-600">
              <span>Discount ({discountPercent}%)</span>
              <span>−{formatCedi(discountAmount)}</span>
            </div>
          ) : (
            <div className="flex justify-between text-xs text-amber-600">
              <span>Discount requested ({discountPercent}%)</span>
              <span>Pending approval</span>
            </div>
          )
        )}
        {posPaymentMethod && posPaymentMethod !== 'split' && (
            <div className="flex justify-between text-xs text-gray-500">
              <span>Payment</span>
              <span>{methodLabels[posPaymentMethod]}</span>
            </div>
          )}
          {posPaymentMethod === 'split' && splitPayments.length > 0 && splitPayments.map((payment, index) => (
            <div key={`${payment.method}-${index}`} className="flex justify-between text-xs text-gray-500">
              <span>{methodLabels[payment.method]}</span>
              <span>{formatCedi(payment.amount)}</span>
            </div>
          ))}
          <div className="order-total-row">
            <div>
              <div className="order-items-summary">Items ({totalItems}) · {formatCedi(grandTotal - packagingCost)}</div>
              <span className="order-total-label">Total</span>
            </div>
            <span className="order-total-amount">{formatCedi(grandTotal)}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={processOrder}
          disabled={cartIsEmpty || isProcessing}
          className="place-order-btn"
        >
          {isProcessing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              <span>Processing...</span>
            </>
          ) : (
            <>
              <span>Place Order</span>
              <span>|</span>
              <span>{formatCedi(grandTotal)}</span>
            </>
          )}
        </button>
      </div>
    </>
  );

  return (
    <div className="-mx-4 sm:-mx-6 lg:-mx-8 -my-5 sm:-my-8 pos-tab-shell">
      {readyOrders.length > 0 && (
        <div className="border-b px-4 sm:px-6 py-3" style={{ backgroundColor: 'var(--bg-surface-hover)', borderColor: 'var(--border-primary)' }}>
          <div className="flex flex-col gap-3 mb-2 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--accent)' }} />
              <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{readyOrders.length} Ready for Pickup</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-medium" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox"
                  checked={areAllReadyOrdersSelected}
                  onChange={toggleSelectAllReadyOrders}
                  className="h-4 w-4 rounded border-gray-300 accent-[#B42318]"
                />
                Select all
              </label>
              {selectedReadyOrderIds.size > 0 && (
                <button
                  type="button"
                  onClick={() => completeReadyOrders([...selectedReadyOrderIds])}
                  className="action-btn action-btn-primary action-btn-sm"
                >
                  Complete selected ({selectedReadyOrderIds.size})
                </button>
              )}
              <button
                type="button"
                onClick={() => completeReadyOrders(readyOrders.map((order) => order.id))}
                className="action-btn action-btn-secondary action-btn-sm"
              >
                Complete all
              </button>
            </div>
          </div>
          <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-1">
            {readyOrders.map((order) => {
              const waitMin = (now - new Date(order.readyAt).getTime()) / 60000;
              const assignedRider = isDeliveryOrder(order) ? getOrderAssignedRider(order) : null;
              return (
                <div
                  key={order.id}
                  className="flex-shrink-0 rounded-xl px-4 py-3 border shadow-sm flex items-center gap-4 min-w-[280px]"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    borderColor: waitMin > 5 ? '#d97706' : 'var(--border-primary)',
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-bold text-gray-800 text-sm">#{order.id.slice(-6)}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-md font-medium" style={{ backgroundColor: 'var(--bg-surface-hover)', color: 'var(--text-secondary)' }}>
                          {order.orderType === 'in-person' ? 'Pickup' : 'Delivery'}
                        </span>
                      </div>
                      <input
                        type="checkbox"
                        checked={selectedReadyOrderIds.has(order.id)}
                        onChange={() => toggleReadyOrderSelection(order.id)}
                        className="h-4 w-4 rounded border-gray-300 accent-[#B42318]"
                        aria-label={`Select ready order ${order.id.slice(-6)}`}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {order.customerName || 'Walk-in'} | {formatCedi(order.total)} | {relativeTimeFrom(order.readyAt)}
                    </div>
                    {assignedRider && (
                      <div className="text-xs mt-1 font-medium" style={{ color: 'var(--text-secondary)' }}>
                        Rider: {assignedRider.riderName}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      type="button"
                      onClick={() => showOrderDetailsModal(order)}
                      className="action-btn action-btn-secondary action-btn-sm"
                    >
                      View
                    </button>
                    {isDeliveryOrder(order) && (
                      <button
                        type="button"
                        onClick={() => openRiderAssignmentModal(order, { markReady: false })}
                        className="action-btn action-btn-secondary action-btn-sm"
                      >
                        {assignedRider ? 'Edit rider' : 'Assign rider'}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => completeReadyOrders([order.id])}
                      className="action-btn action-btn-primary action-btn-sm"
                    >
                      {getReadyOrderCompletionLabel(order)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className={classNames('pos-workspace', isMobile ? 'min-h-full flex-col' : 'h-[calc(100vh-56px)]')}>
        <section className="pos-menu-shell flex-1 flex flex-col min-w-0" style={{ backgroundColor: 'var(--bg-base)' }}>
          <div className="pos-shell-topbar border-b px-4 sm:px-6 py-3 space-y-3" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
                <div className="flex rounded-lg p-1 pos-order-type-group" style={{ backgroundColor: 'var(--bg-surface-hover)' }}>
                  <button
                    type="button"
                    onClick={() => setOrderType('online')}
                    className={classNames(
                      'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all',
                      orderType === 'online' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    <PosUiIcon name="online" className="h-4 w-4" />
                    Online
                  </button>
                  <button
                    type="button"
                    onClick={() => setOrderType('in-person')}
                    className={classNames(
                      'flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap transition-all',
                      orderType === 'in-person' ? 'bg-white text-indigo-700 shadow-sm' : 'text-gray-500 hover:text-gray-700',
                    )}
                  >
                    <PosUiIcon name="store" className="h-4 w-4" />
                    In-Person
                  </button>
                </div>

                <div className="hidden h-6 w-px bg-gray-200 lg:block" />

                <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1 max-w-full pos-bundle-strip">
                  {currentBundles.map((bundle) => (
                    <button
                      key={bundle.id}
                      type="button"
                      onClick={() => setActiveBundleId(bundle.id)}
                      className={classNames(
                        'max-w-[9rem] rounded-lg border px-3 py-1.5 text-xs font-medium transition-all pos-bundle-chip',
                        bundle.id === activeBundleId ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm' : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300',
                      )}
                    >
                      <span className="inline-block max-w-[6.5rem] truncate align-bottom">{bundle.name}</span>
                      <span className={classNames('ml-1 text-[10px]', bundle.id === activeBundleId ? 'text-white/80' : 'text-gray-400')}>
                        {Object.values(bundle.items).reduce((sum, item) => sum + (item.quantity || 0), 0)}
                      </span>
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={addNewBundle}
                    className="px-2 py-1.5 rounded-lg text-xs font-medium bg-white text-gray-500 border border-dashed border-gray-300 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                  >
                    + Package
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:flex-wrap">
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</span>
              </div>
              <div className="flex items-center gap-1.5 overflow-x-auto custom-scrollbar pb-1 sm:pb-0">
                {ORDER_SOURCES.map((source) => {
                  const isOnline = source.key === 'online';
                  const isActive = orderSource === source.key;
                  return (
                    <button
                      key={source.key}
                      type="button"
                      ref={isOnline ? onlineSourceBtnRef : undefined}
                      onClick={() => {
                        setOrderSource(source.key);
                        if (isOnline) openChannelMenu();
                        else setChannelMenuPos(null);
                      }}
                      className={classNames(
                        'px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap border transition-all',
                        isActive
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300 hover:text-indigo-600',
                      )}
                      aria-haspopup={isOnline ? 'menu' : undefined}
                      aria-expanded={isOnline ? Boolean(channelMenuPos) : undefined}
                    >
                      {isOnline && isActive && orderSourceDetail
                        ? `Online · ${orderSourceDetail} ▾`
                        : isOnline ? 'Online ▾' : source.label}
                    </button>
                  );
                })}
              </div>
              {channelMenuPos && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    aria-hidden="true"
                    onClick={() => setChannelMenuPos(null)}
                  />
                  <div
                    role="menu"
                    aria-label="Online channel (required)"
                    className="fixed z-50 w-40 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
                    style={{ left: channelMenuPos.left, top: channelMenuPos.top }}
                  >
                    <div className="px-3 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Channel *</div>
                    {ONLINE_SOURCE_CHANNELS.map((channel) => (
                      <button
                        key={channel}
                        type="button"
                        role="menuitem"
                        onClick={() => { setOrderSourceDetail(channel); setChannelMenuPos(null); }}
                        className={classNames(
                          'w-full text-left px-3 py-1.5 text-xs hover:bg-indigo-50 hover:text-indigo-700',
                          orderSourceDetail === channel ? 'text-indigo-700 font-semibold' : 'text-gray-700',
                        )}
                      >
                        {channel}
                      </button>
                    ))}
                  </div>
                </>
              )}
              {orderSource === 'other' && (
                <input
                  type="text"
                  value={orderSourceDetail}
                  onChange={(e) => setOrderSourceDetail(sanitizeText(e.target.value, 60))}
                  className="px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Specify source *"
                  aria-label="Specify order source (required)"
                  maxLength={60}
                />
              )}
            </div>

            {orderType === 'online' && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-5">
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(sanitizeText(e.target.value, 100))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Customer name *"
                  aria-label="Customer name (required)"
                  required
                  maxLength={100}
                />
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(sanitizeText(e.target.value, 20))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Contact number *"
                  aria-label="Contact number (required)"
                  required
                  maxLength={20}
                />
                <input
                  type="text"
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(sanitizeText(e.target.value, 300))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Delivery address *"
                  aria-label="Delivery address (required)"
                  required
                  maxLength={300}
                />
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={deliveryFeeInput}
                  onChange={(e) => setDeliveryFeeInput(sanitizeNumeric(e.target.value, { min: 0, max: 99999, fallback: '' }))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Delivery fee"
                />
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(sanitizeText(e.target.value, 500))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Notes (optional)"
                  maxLength={500}
                />
              </div>
            )}

            {orderType === 'in-person' && (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(sanitizeText(e.target.value, 100))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Customer name (optional)"
                  aria-label="Customer name (optional)"
                  maxLength={100}
                />
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(sanitizeText(e.target.value, 20))}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  placeholder="Contact number (optional)"
                  aria-label="Contact number (optional)"
                  maxLength={20}
                />
              </div>
            )}

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center pos-filter-row">
              <div className="flex items-center gap-2 overflow-x-auto custom-scrollbar pb-1 sm:pb-0 flex-shrink-0 pos-filter-pills">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setActiveCategory(category.id)}
                    className={classNames(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all',
                      activeCategory === category.id ? 'bg-indigo-600 text-white shadow-sm' : 'bg-white text-gray-600 border border-gray-200 hover:border-indigo-300 hover:text-indigo-600',
                    )}
                  >
                    <PosUiIcon name={category.icon} className="h-4 w-4" />
                    {category.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 relative min-w-0">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  <PosUiIcon name="search" className="h-4 w-4" />
                </span>
                <input
                  type="text"
                  value={menuSearch}
                  onChange={(e) => setMenuSearch(e.target.value)}
                  placeholder="Search menu..."
                  className="w-full pl-9 pr-8 py-2 border rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  style={{ backgroundColor: 'var(--bg-input)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
                />
                {menuSearch && (
                  <button
                    type="button"
                    onClick={() => setMenuSearch('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <PosUiIcon name="close" className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="pos-menu-area flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-5 space-y-5 sm:space-y-6">
            {(activeCategory === 'all' || activeCategory === 'promotions') && filteredPromotions.length > 0 &&
              renderMenuSection('Promotions', filteredPromotions, 'promotions')}
            {(activeCategory === 'all' || activeCategory === 'mainPackages') && filteredMain.length > 0 &&
              renderMenuSection('Main Packages', filteredMain, 'mainPackages')}
            {(activeCategory === 'all' || activeCategory === 'extras') && filteredExtras.length > 0 &&
              renderMenuSection('Extras & Add-ons', filteredExtras, 'extras')}

            {menuSearch && filteredPromotions.length === 0 && filteredMain.length === 0 && filteredExtras.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg" style={{ backgroundColor: 'var(--bg-surface-hover)', color: 'var(--text-tertiary)' }}>
                  <PosUiIcon name="search" className="h-6 w-6" />
                </span>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>No items match "{menuSearch}"</p>
                <button type="button" onClick={() => setMenuSearch('')} className="mt-3 text-indigo-600 text-sm font-medium hover:underline">
                  Clear search
                </button>
              </div>
            )}
          </div>
        </section>

        {!isMobile && (
          <aside className="pos-order-panel border-l flex flex-col" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
            <div className="px-4 py-4 md:px-5 border-b flex items-center justify-between gap-3" style={{ borderColor: 'var(--border-primary)' }}>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-bold text-gray-800">Order</h3>
                {totalItems > 0 && (
                  <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">{totalItems}</span>
                )}
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap" style={{ backgroundColor: 'var(--bg-surface-hover)', color: 'var(--text-secondary)' }}>
                  {orderType === 'online' ? 'Online' : 'In-Person'}
                </span>
                {totalItems > 0 && (
                  <button type="button" onClick={clearCurrentOrder} className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors">
                    Clear
                  </button>
                )}
              </div>
            </div>
            {renderCart()}
          </aside>
        )}
      </div>

      {isMobile && (
        <OrderFAB itemCount={totalItems} total={grandTotal} onOpen={() => setShowMobileCart(true)} />
      )}

      {isMobile && (
        <OrderDrawer isOpen={showMobileCart} onClose={() => setShowMobileCart(false)}>
          <div className="flex items-center justify-between px-1 pb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-bold text-gray-800">Order</h3>
              {totalItems > 0 && (
                <span className="bg-indigo-100 text-indigo-700 text-xs font-bold px-2 py-0.5 rounded-full">{totalItems}</span>
              )}
            </div>
            <button type="button" onClick={() => setShowMobileCart(false)} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200">
              <PosUiIcon name="close" className="h-4 w-4" />
            </button>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs font-medium" style={{ borderColor: 'var(--border-primary)', backgroundColor: 'var(--bg-surface-hover)', color: 'var(--text-secondary)' }}>
            <span className="rounded-full px-2.5 py-1 whitespace-nowrap" style={{ backgroundColor: 'var(--bg-elevated)', color: 'var(--text-secondary)' }}>
              {orderType === 'online' ? 'Online' : 'In-Person'}
            </span>
            {totalItems > 0 && (
              <button type="button" onClick={clearCurrentOrder} className="text-red-400 hover:text-red-600 transition-colors">
                Clear
              </button>
            )}
          </div>
          {renderCart()}
        </OrderDrawer>
      )}
    </div>
  );
}
